// src/services/langPrefs.js
import { LANG_HALF_LIFE_MIN, LANG_TOP_K } from '../config/featureFlags';

// Persist long-term in localStorage; short-term is session-only.
const STORAGE_KEY = 'langPrefs.long.v1';

// Initialize 'long' from (priority) UI language > device locale > geoloc heuristic.
// Fallback to equal weights if unknown.
function initialLong() {
  const saved = safeLoad();
  if (saved) return saved;

  const ui = detectUiLang();            // implement detection using your app's i18n or a prop; fallback navigator.language
  const seed = { TH: 0.34, EN: 0.33, ZH: 0.33 };
  if (ui) {
    for (const k of Object.keys(seed)) seed[k] = 0.33;
    seed[ui] = 0.5;         // slight head-start for UI language
    renorm(seed);
  }
  safeSave(seed);
  return seed;
}

let long = initialLong();
let short = {}; // session deltas only

// --- Utils ---
function renorm(v) {
  const s = Object.values(v).reduce((a,b)=>a+(b||0),0) || 1;
  for (const k of Object.keys(v)) v[k] = (v[k]||0) / s;
}

function add(a, b) {
  const out = {...a};
  for (const k of Object.keys(b)) out[k] = (out[k]||0) + (b[k]||0);
  return out;
}

function clamp(v, lo=0, hi=1) {
  return Math.max(lo, Math.min(hi, v));
}

function safeLoad() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeSave(v) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {}
}

function detectUiLang() {
  // If you have a global i18n context, read from there; else use navigator.language prefix.
  const nav = (navigator?.language || '').toUpperCase();
  if (nav.startsWith('TH')) return 'TH';
  if (nav.startsWith('EN')) return 'EN';
  if (nav.startsWith('ZH') || nav.startsWith('CN')) return 'ZH';
  return undefined;
}

// --- Public API ---
// Boost short-term for language 'code' by 'amt' and cap at +2.0 per session to avoid runaway.
export function boostLang(code, amt) {
  if (!code || !isFinite(amt)) return;
  short[code] = clamp((short[code]||0) + amt, -2, 2);
}

export function decayShort() {
  // half-life decay: short *= 0.5 every LANG_HALF_LIFE_MIN
  for (const k of Object.keys(short)) short[k] *= 0.5;
}

export function foldShortIntoLong(f = 0.1) {
  // move a small fraction of short-term into long-term and persist long.
  for (const k of Object.keys(short)) {
    const delta = (short[k]||0) * f;
    long[k] = clamp((long[k]||0) + delta, 0, 1);
  }
  renorm(long);
  safeSave(long);
}

export function langEff() {
  const eff = add(long, short);
  renorm(eff);
  return eff;
}

export function topLangs(eff, k = LANG_TOP_K) {
  return Object.entries(eff)
    .sort((a,b)=> (b[1]||0)-(a[1]||0))
    .slice(0, k)
    .map(([code, w]) => ({ code, w }));
}

// Optional: expose read-only getters for debugging/telemetry
export function getLong() {
  return {...long};
}

export function getShort() {
  return {...short};
}

// Milestone 1.5 additions: setPrimaryLang and onExplicitUiLangSwitch
export function setPrimaryLang(code) {
  // Make selected language dominant (e.g., 0.8) and renormalize others
  const target = code || 'EN';
  const keys = new Set([...Object.keys(getLong()), target, 'TH', 'EN', 'ZH']);
  const nextLong = {};
  // give dominance to target; split the rest evenly
  const rest = Array.from(keys).filter(k => k !== target);
  nextLong[target] = 0.8;
  const share = rest.length ? (0.2 / rest.length) : 0;
  for (const k of rest) nextLong[k] = share;
  // persist
  long = nextLong;
  // clear short-term noise so the switch is crisp
  short = {};
  safeSave(nextLong);
}

export function onExplicitUiLangSwitch(code) {
  // strong short-term boost + make it primary
  boostLang(code, 0.5);
  setPrimaryLang(code);
}

// Kick off periodic decay timer (clean up in app unmount if needed)
let _timer = null;

export function startLangDecayTimer() {
  stopLangDecayTimer();
  _timer = setInterval(decayShort, Math.max(1, LANG_HALF_LIFE_MIN) * 60 * 1000);
}

export function stopLangDecayTimer() {
  if (_timer) {
    clearInterval(_timer);
    _timer = null;
  }
}
